import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { HandCoins, FileDown, CheckCircle2, AlertTriangle, Scale, Undo2, Settings as SettingsIcon, Table2, Pencil, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import DateRangeBar from '../components/DateRangeBar';
import { downloadDriverStatementPDF, downloadExcel } from '../utils/exportHelpers';

const R = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
const day = (d) => (d ? new Date(d).toLocaleDateString('en-ZA') : '—');
const kg = (v) => (v == null || v === '' ? '—' : `${parseFloat(v).toFixed(1)}`);
const iso = (d) => { const t = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return t.toISOString().split('T')[0]; };

// Weight and size, edited in the row. Saves on Enter or when the focus leaves
// the group; the statement re-rates the row from the server's answer.
function WeightCells({ row, onSaved }) {
  const [v, setV] = useState({ weight: row.weight ?? '', length: row.length ?? '', width: row.width ?? '', height: row.height ?? '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV({ weight: row.weight ?? '', length: row.length ?? '', width: row.width ?? '', height: row.height ?? '' }); },
    [row.weight, row.length, row.width, row.height]);

  const dirty = ['weight', 'length', 'width', 'height'].some((k) => String(v[k] ?? '') !== String(row[k] ?? ''));
  const save = async () => {
    if (!dirty) return;
    const w = parseFloat(v.weight) || 0, L = parseFloat(v.length) || 0, W = parseFloat(v.width) || 0, H = parseFloat(v.height) || 0;
    if (w <= 0 && !(L > 0 && W > 0 && H > 0)) { toast.error('Enter a weight, or all three sizes'); return; }
    setBusy(true);
    try {
      await api.patch(`/parcels/${row.tracking_number}/weight`, { weight: w || null, length: L || null, width: W || null, height: H || null });
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save the weight'); }
    finally { setBusy(false); }
  };
  const onKey = (e) => { if (e.key === 'Enter') e.target.blur(); };
  const onBlur = (e) => {
    // leaving the whole group, not hopping between its four boxes
    if (!e.currentTarget.contains(e.relatedTarget)) save();
  };
  const box = (k, ph, w = 'w-14') => (
    <input type="number" step="0.1" min="0" value={v[k]} placeholder={ph} onKeyDown={onKey} disabled={busy}
      onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))}
      className={`${w} border rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-orange-400 ${row.needs_weight ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`} />
  );
  return (
    <div className="flex items-center gap-1" onBlur={onBlur}>
      {box('weight', 'kg')}
      <span className="text-gray-300 text-xs">|</span>
      {box('length', 'L', 'w-12')}<span className="text-gray-300 text-[10px]">×</span>
      {box('width', 'W', 'w-12')}<span className="text-gray-300 text-[10px]">×</span>
      {box('height', 'H', 'w-12')}
      {dirty && <span className="text-[10px] text-orange-600 ml-1">{busy ? 'saving…' : 'Enter'}</span>}
    </div>
  );
}

// The amount cell: the table rate, or a manual amount the office typed for
// this parcel (an oversize load, a special trip). Click the pencil to set or
// clear it; the server re-rates the row.
function AmountCell({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const start = () => { setAmount(row.manual_rate != null ? String(row.manual_rate) : ''); setNote(row.manual_note || ''); setOpen(true); };
  const save = async (clear = false) => {
    setBusy(true);
    try {
      await api.patch(`/driver-pay/parcels/${row.tracking_number}/manual`, { amount: clear ? null : amount, note });
      toast.success(clear ? 'Back to the weight table' : `Manual amount ${R(amount)} saved`);
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };
  if (open) {
    return (
      <div className="flex items-center justify-end gap-1" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter') save(); }}>
        <input type="number" step="0.01" min="0" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="R"
          className="w-20 border border-orange-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="why (optional)"
          className="w-28 border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <button onClick={() => save(false)} disabled={busy || amount === ''} title="Save" className="p-1 rounded bg-green-500 hover:bg-green-600 text-white disabled:opacity-40"><Check size={12} /></button>
        {row.manual_rate != null && <button onClick={() => save(true)} disabled={busy} title="Remove the manual amount — use the weight table" className="p-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-semibold px-1.5">table</button>}
        <button onClick={() => setOpen(false)} className="p-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-600"><X size={12} /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-end gap-1">
      {row.rate == null ? <span className="text-amber-600 text-xs">needs weight</span> : <span className="font-semibold">{R(row.rate)}</span>}
      {row.manual_rate != null && (
        <span className="text-[10px] px-1.5 py-px rounded bg-violet-100 text-violet-700 font-semibold" title={`Manual amount${row.manual_note ? `: ${row.manual_note}` : ''}${row.table_rate != null ? ` · table would pay ${R(row.table_rate)}` : ''}`}>manual</span>
      )}
      <button onClick={start} title="Set a manual amount for this parcel" className="p-0.5 rounded text-gray-300 hover:text-orange-500 hover:bg-orange-50"><Pencil size={11} /></button>
    </div>
  );
}

function LegChip({ leg }) {
  return leg === 'pickup'
    ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">Pickup</span>
    : <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700 border border-green-200">Delivery</span>;
}

function MarkPaidModal({ driver, from, to, totals, onClose, onDone }) {
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/driver-pay/payments', { driver_id: driver.id, from, to, reference, note });
      toast.success(`Marked ${totals.parcels} parcels paid — ${R(totals.total)}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not mark as paid'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 m-auto my-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-gray-800 text-lg">Mark as paid</h3>
        <p className="text-sm text-gray-600 mt-1">
          <strong>{driver.name}</strong> · {day(from)} – {day(to)} · {totals.parcels} parcels · <strong>{R(totals.total)}</strong>
        </p>
        <p className="text-xs text-gray-500 mt-1">These parcels will not appear on a statement again. You can undo from the Paid statements list.</p>
        <label className="block text-xs font-medium text-gray-500 mt-4 mb-1">Payment reference (EFT ref, cash, …)</label>
        <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. EFT 12 Oct"
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <label className="block text-xs font-medium text-gray-500 mt-3 mb-1">Note</label>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-gray-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Mark paid'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriverPay() {
  const qc = useQueryClient();
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [driverId, setDriverId] = useState('');
  const [paying, setPaying] = useState(false);

  const { data: drivers = [] } = useQuery({
    queryKey: ['driver-pay-drivers'],
    queryFn: () => api.get('/driver-pay/drivers').then((r) => r.data),
  });
  useEffect(() => { if (!driverId && drivers.length) setDriverId(drivers[0].id); }, [drivers, driverId]);
  const driver = drivers.find((d) => d.id === driverId);

  const { data: settings = {} } = useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => api.get('/settings/billing').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: st, isLoading, error } = useQuery({
    queryKey: ['driver-pay-statement', driverId, from, to],
    queryFn: () => api.get('/driver-pay/statement', { params: { driver_id: driverId, from, to } }).then((r) => r.data),
    enabled: !!driverId,
    staleTime: 0,
  });
  const { data: payments = [] } = useQuery({
    queryKey: ['driver-pay-payments', driverId],
    queryFn: () => api.get('/driver-pay/payments', { params: { driver_id: driverId } }).then((r) => r.data),
    enabled: !!driverId,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['driver-pay-statement'] });
    qc.invalidateQueries({ queryKey: ['driver-pay-payments'] });
  };

  const rows = st?.rows || [];
  const totals = st?.totals || { parcels: 0, needs_weight: 0, chargeable_kg: 0, total: 0 };

  const pdf = () => downloadDriverStatementPDF({
    driver_name: driver?.name, driver_email: driver?.email, driver_phone: driver?.phone,
    period_from: from, period_to: to, rows, total: totals.total, rates: st?.rates,
  }, settings);
  const excel = () => downloadExcel(rows, [
    { header: 'Date', value: (r) => day(r.handled_at), width: 12 },
    { header: 'Leg', value: (r) => r.leg, width: 10 },
    { header: 'Tracking', value: (r) => r.tracking_number, width: 12 },
    { header: 'From', value: (r) => r.sender, width: 28 },
    { header: 'To', value: (r) => r.recipient, width: 28 },
    { header: 'Actual kg', value: (r) => r.actual_kg, width: 10 },
    { header: 'Volumetric kg', value: (r) => r.volumetric_kg, width: 12 },
    { header: 'Chargeable kg', value: (r) => r.chargeable_kg, width: 12 },
    { header: 'Amount', value: (r) => r.rate, width: 10 },
    { header: 'Note', value: (r) => (r.manual_rate != null ? `Manual amount${r.manual_note ? `: ${r.manual_note}` : ''}` : ''), width: 28 },
  ], `driver-pay-${(driver?.name || 'driver').replace(/\s+/g, '-')}-${from}-${to}.xlsx`, 'Driver pay');

  const paidPdf = async (p) => {
    try {
      const { data } = await api.get(`/driver-pay/payments/${p.id}`);
      await downloadDriverStatementPDF({
        driver_name: data.driver_name, driver_email: data.driver_email, driver_phone: data.driver_phone,
        period_from: data.period_from, period_to: data.period_to, rows: data.rows, total: data.total,
        paid_at: data.paid_at, reference: data.reference,
      }, settings);
    } catch (e) { toast.error('Could not load that statement'); }
  };
  const undo = async (p) => {
    if (!window.confirm(`Undo the payment of ${R(p.total)} for ${day(p.period_from)} – ${day(p.period_to)}? Its ${p.parcel_count} parcels become unpaid again.`)) return;
    try { await api.delete(`/driver-pay/payments/${p.id}`); toast.success('Payment undone'); refresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not undo'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <HandCoins size={22} className="text-orange-500" />
            <h1 className="text-xl font-bold text-gray-800">Driver Pay</h1>
          </div>
          <p className="text-sm text-gray-500 ml-9">Parcels an outsourced driver handled, rated by weight, and what has been paid.</p>
        </div>
        <Link to="/settings?tab=driverpay" className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-orange-600 border rounded-lg px-3 py-2">
          <SettingsIcon size={15} /> Rates
        </Link>
      </div>

      {drivers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
          <p className="font-medium text-gray-700">No outsourced drivers yet</p>
          <p className="text-sm mt-1">Add a driver and their price list under <Link to="/settings?tab=driverpay" className="text-orange-600 underline">Settings → Driver Pay</Link>.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Driver</label>
                <select value={driverId} onChange={(e) => setDriverId(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[280px]">
                <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />
              </div>
            </div>
            {driver && (
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <Table2 size={13} />
                {driver.table.slice(0, 4).map((b) => `${b.from_kg}–${b.to_kg} kg ${R(b.rate)}`).join(' · ')} · … then +{R(driver.step_rate)} per {parseFloat(driver.step_kg)} kg
                {driver.over_kg ? `; above ${parseFloat(driver.over_kg)} kg +${R(driver.over_rate)} per kg` : ''}. Chargeable weight = actual or L×W×H÷5000, whichever is more. Click the pencil on an amount to pay a manual amount for that parcel instead.
              </p>
            )}
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ['Parcels', totals.parcels, ''],
              ['Chargeable kg', kg(totals.chargeable_kg), ''],
              ['Need a weight', totals.needs_weight, totals.needs_weight ? 'text-amber-600' : 'text-gray-800'],
              ['To pay', R(totals.total), 'text-green-700'],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-white rounded-xl shadow-sm p-4">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{l}</p>
                <p className={`text-2xl font-bold mt-1 ${c || 'text-gray-800'}`}>{v}</p>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={pdf} disabled={!rows.length}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold">
              <FileDown size={15} /> Statement PDF
            </button>
            <button onClick={excel} disabled={!rows.length}
              className="flex items-center gap-2 border border-gray-300 hover:border-gray-800 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40">
              Excel
            </button>
            <button onClick={() => setPaying(true)} disabled={!rows.length || totals.needs_weight > 0}
              title={totals.needs_weight ? `${totals.needs_weight} parcels still need a weight` : ''}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-semibold">
              <CheckCircle2 size={15} /> Mark as paid
            </button>
            {totals.needs_weight > 0 && (
              <span className="flex items-center gap-1 text-xs text-amber-700"><AlertTriangle size={13} /> Fill in the amber weights before paying.</span>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
            {error ? (
              <p className="p-6 text-sm text-red-600">{error.response?.data?.error || 'Could not load the statement'}</p>
            ) : isLoading ? (
              <p className="p-6 text-sm text-gray-400">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="p-6 text-sm text-gray-400">No unpaid parcels for {driver?.name} between {day(from)} and {day(to)}.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Leg</th>
                    <th className="text-left px-3 py-2">Tracking</th>
                    <th className="text-left px-3 py-2">From → To</th>
                    <th className="text-left px-3 py-2"><span className="flex items-center gap-1"><Scale size={12} /> kg | L × W × H (cm)</span></th>
                    <th className="text-right px-3 py-2">Vol. kg</th>
                    <th className="text-right px-3 py-2">Charge kg</th>
                    <th className="text-right px-3 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.parcel_id} className={`border-t border-gray-100 ${r.needs_weight ? 'bg-amber-50/60' : ''}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{day(r.handled_at)}</td>
                      <td className="px-3 py-2"><LegChip leg={r.leg} />{r.both_legs && <span className="ml-1 text-[10px] text-amber-700" title="This driver did both the pickup and the delivery — paid once">both</span>}</td>
                      <td className="px-3 py-2 font-mono font-semibold"><Link to={`/parcels/${r.tracking_number}`} className="hover:text-orange-600">{r.tracking_number}</Link></td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={`${r.sender} → ${r.recipient}`}>{r.sender || '—'} <span className="text-gray-300">→</span> {r.recipient || '—'}</td>
                      <td className="px-3 py-2"><WeightCells row={r} onSaved={refresh} /></td>
                      <td className="px-3 py-2 text-right text-gray-500">{kg(r.volumetric_kg)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{kg(r.chargeable_kg)}</td>
                      <td className="px-3 py-2 text-right"><AmountCell row={r} onSaved={refresh} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-semibold">
                  <tr>
                    <td className="px-3 py-2" colSpan={6}>{totals.parcels} parcels</td>
                    <td className="px-3 py-2 text-right">{kg(totals.chargeable_kg)}</td>
                    <td className="px-3 py-2 text-right text-green-700">{R(totals.total)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Paid statements */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-700 text-sm">Paid statements — {driver?.name}</h3>
            </div>
            {payments.length === 0 ? (
              <p className="p-4 text-sm text-gray-400">Nothing paid yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Paid on</th>
                    <th className="text-left px-3 py-2">Period</th>
                    <th className="text-right px-3 py-2">Parcels</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">Reference</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 whitespace-nowrap">{day(p.paid_at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{day(p.period_from)} – {day(p.period_to)}</td>
                      <td className="px-3 py-2 text-right">{p.parcel_count}</td>
                      <td className="px-3 py-2 text-right font-semibold">{R(p.total)}</td>
                      <td className="px-3 py-2 text-gray-600">{p.reference || '—'}{p.note ? <span className="text-gray-400"> · {p.note}</span> : null}</td>
                      <td className="px-3 py-2 text-gray-500">{p.paid_by_name || '—'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => paidPdf(p)} className="text-gray-600 hover:text-orange-600 mr-3" title="Download statement"><FileDown size={15} /></button>
                        <button onClick={() => undo(p)} className="text-gray-400 hover:text-red-600" title="Undo this payment"><Undo2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {paying && driver && (
        <MarkPaidModal driver={driver} from={from} to={to} totals={totals}
          onClose={() => setPaying(false)} onDone={() => { setPaying(false); refresh(); }} />
      )}
    </div>
  );
}
